import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { CustomFieldTypeName } from 'src/app/custom-fields/custom-fields.model';
import { CustomFieldsService } from 'src/app/custom-fields/custom-fields.service';
import { Choice } from 'src/app/shared/components/select/choice.model';
import { BasicService } from 'src/app/shared/services/basic.service';
import { CRMType } from 'src/app/user-profile/shared/models';

@Component({
  selector: 'app-crm-synchronization-conversation-agent-workflow',
  templateUrl: './crm-synchronization-conversation-agent-workflow.component.html',
  styleUrls: ['./crm-synchronization-conversation-agent-workflow.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class CrmSynchronizationConversationAgentWorkflowComponent implements OnInit, OnDestroy {
  @Input() crmType: CRMType;
  @Input() triggerMsgRoutedToCrmTeam: any;
  @Input() triggerMsgRepliedByAgentAndUserMatched: any;
  @Input() configuration: any;
  @Output() agentWorkflowChanged: EventEmitter<any> = new EventEmitter();
  @Output() zendeskContinuousSynchronizationChanged: EventEmitter<any> = new EventEmitter();

  fromAlcmeonEnabledControl = new FormControl();
  fromAlcmeonPushToCrmTriggersControl = new FormControl();
  fromCRMEnabledControl = new FormControl();
  continuousSynchronizationEnabledControl: FormControl = new FormControl(false);

  routingTagsItems = [];

  zendeskControl = new FormGroup({
    teamTagIds: new FormControl([], Validators.required),
    emailCustomField: new FormControl(null, Validators.required),
    enableContinuousSynchronization: new FormControl(false),
  });

  customFieldsLink: string = this.basicService.fullURLForRoutePath(
    `/#/${this.alcmeonContextService.companyId}/fuse/parameters/custom-field?requestedPage=home`
  );

  emailCustomFields: Choice[] = [];

  private subscriptions = new Subscription();

  constructor(
    private basicService: BasicService,
    private alcmeonContextService: AlcmeonContextService,
    customFieldsService: CustomFieldsService,
    @Inject('DropdownItemsFactory') DropdownItemsFactory: any,
    @Inject('TopToolbarFactory') private topToolbarService: any
  ) {
    const routingTags = this.alcmeonContextService
      .getCompany()
      .tags_groups.filter(tag_group => tag_group.is_routing === 1);
    this.routingTagsItems = DropdownItemsFactory.buildTagItems(routingTags)[0].items.flatMap(i => i.subitems);

    customFieldsService.getFields().subscribe(customfields => {
      this.emailCustomFields = customfields
        .filter(({ type }) => type.type === CustomFieldTypeName.Email)
        .map(({ id, label }) => ({ id, label }));
    });
  }

  ngOnInit(): void {
    switch (this.crmType) {
      case 'salesforce': {
        this.initializeSalesforceFields();
        this.listenSalesforceSettings();
        break;
      }
      case 'zendesk': {
        this.initializeZendeskFields();
        this.listenZendeskSettings();
        break;
      }
      default:
        break;
    }
  }

  private listenSalesforceSettings() {
    this.subscriptions.add(
      this.fromAlcmeonEnabledControl.valueChanges.subscribe(value => {
        this.configuration.agents_workflow.from_alcmeon.enabled = value;
        if (!value) {
          this.fromAlcmeonPushToCrmTriggersControl.disable();
        } else {
          this.fromAlcmeonPushToCrmTriggersControl.enable();
        }
        this.agentWorkflowChanged.emit(this.configuration.agents_workflow);
      })
    );
    this.subscriptions.add(
      this.fromAlcmeonPushToCrmTriggersControl.valueChanges.subscribe(value => {
        this.configuration.agents_workflow.from_alcmeon.push_to_crm_triggers = value;
        this.agentWorkflowChanged.emit(this.configuration.agents_workflow);
      })
    );

    this.subscriptions.add(
      this.fromCRMEnabledControl.valueChanges.subscribe(enabled => {
        this.configuration.agents_workflow.from_sf.enabled = enabled;
        this.agentWorkflowChanged.emit(this.configuration.agents_workflow);
      })
    );
  }

  private listenZendeskSettings() {
    this.subscriptions.add(
      this.fromAlcmeonEnabledControl.valueChanges.subscribe(enabled => {
        this.configuration.agents_workflow.from_alcmeon.enabled = enabled;
        if (enabled) {
          this.fromCRMEnabledControl.setValue(false);
        }
        this.agentWorkflowChanged.emit(this.configuration.agents_workflow);
      })
    );

    this.subscriptions.add(
      this.fromCRMEnabledControl.valueChanges.subscribe(enabled => {
        this.zendeskControl.get('enableContinuousSynchronization').setValue(enabled);
        this.configuration.agents_workflow.from_sf.enabled = enabled;
        if (enabled) {
          this.fromAlcmeonEnabledControl.setValue(false);
        }
        if (!enabled) {
          this.zendeskControl.disable();
        } else {
          this.zendeskControl.enable();
        }
        this.agentWorkflowChanged.emit(this.configuration.agents_workflow);
      })
    );

    this.subscriptions.add(
      this.zendeskControl.get('teamTagIds').valueChanges.subscribe(values => {
        const teams = (values || []).map((team_tag_id: number) => ({ queue_id: true, team_tag_id }));
        this.configuration.workflow_mappings.from_alcmeon_to_sf = teams;
        this.agentWorkflowChanged.emit(this.configuration.agents_workflow);
      })
    );

    this.subscriptions.add(
      this.zendeskControl.get('emailCustomField').valueChanges.subscribe(value => {
        this.configuration.agents_workflow.from_sf.routing_user_custom_field_ids = [value];
        this.agentWorkflowChanged.emit(this.configuration.agents_workflow);
      })
    );

    this.subscriptions.add(
      this.zendeskControl.get('enableContinuousSynchronization').valueChanges.subscribe(enabled => {
        this.zendeskContinuousSynchronizationChanged.emit(enabled);
      })
    );
  }

  initializeSalesforceFields() {
    this.fromAlcmeonPushToCrmTriggersControl.setValue(
      this.configuration.agents_workflow.from_alcmeon.push_to_crm_triggers
    );
    this.fromAlcmeonEnabledControl.setValue(this.configuration.agents_workflow.from_alcmeon.enabled);
    this.fromCRMEnabledControl.setValue(this.configuration.agents_workflow.from_sf.enabled);
  }

  private initializeZendeskFields() {
    this.fromAlcmeonEnabledControl.setValue(this.configuration.agents_workflow.from_alcmeon.enabled);
    this.fromCRMEnabledControl.setValue(this.configuration.agents_workflow.from_sf.enabled);
    const { sync_mode, agents_workflow, workflow_mappings } = this.configuration;
    const { from_sf, from_alcmeon } = agents_workflow || {};
    const emailCustomField = (from_sf?.routing_user_custom_field_ids || [])[0];
    const teamTagIds = (workflow_mappings?.from_alcmeon_to_sf || []).map(({ team_tag_id }) => team_tag_id);
    this.zendeskControl.patchValue({
      teamTagIds,
      emailCustomField,
      enableContinuousSynchronization: sync_mode?.continous_synchronization_enabled || false,
    });
    if (from_alcmeon?.enabled) {
      this.zendeskControl.disable();
    } else {
      this.zendeskControl.enable();
    }
    this.fromAlcmeonPushToCrmTriggersControl.setValue(
      this.configuration.agents_workflow.from_alcmeon.push_to_crm_triggers
    );
  }

  blinkWorkflowTitle(workflowType) {
    const elementId = workflowType === 'sfToAlcmeon' ? 'crm-to-alcmeon-workflow' : 'alcmeon-to-crm-workflow';
    const workflowMappingTitle = document.getElementById(elementId);
    workflowMappingTitle.scrollIntoView({ behavior: 'smooth' });
    setTimeout(() => workflowMappingTitle.classList.add('alc-blink-workflow'), 500);
    setTimeout(() => workflowMappingTitle.classList.remove('alc-blink-workflow'), 2000);
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }
}
